<template>
  <!-- 
    Started from https://raw.githubusercontent.com/rosendin/vue-bulma-typeahead/master/src/Typeahead.vue
    now really, really different.
  -->
  <span class="vbta">
    <!--
      Using :value and @input instead because watch on query doesn't fire on
      Chrome Android if v-model="query" is used.
    -->
    <input
      :value="query"
      @input="query = $event.target.value"
      ref="inputBox"
      class="input vbta-input"
      type="text"
      :placeholder="placeholder"
      @keyup.delete="handleDelete($event)"
      @keydown.down.prevent="handleKeyDown($event)"
      @keydown.up.prevent="handleKeyUp"
      @keyup.enter.prevent="onMatch()"
      @keyup.esc.prevent="handleEsc"
      @blur="handleBlur">

    <div :class="['vbta-menu', { visible: (foodItemMatches.length || categoryMatches.length) && query }]">
      <ul>
        <!--
          mousedown preventDefault prevents blur on the main input from
          firing, which clears out all the matches before click event handler
          can act on the mouse click
        -->
        <li v-for="match in foodItemMatches" :key="match.id" class="vbta-suggestion"
         @click="onMatch(match)" @mousedown="$event.preventDefault()" @mouseover="handleMouseOver($event)">
          <span class="icon is-size-7">
            <font-awesome-icon :icon="typeIcon(match.type)" />
          </span>
          <span v-html="match.match"></span>
        </li>
        <hr v-if="foodItemMatches.length && categoryMatches.length" data-divider="1" class="divider"/>
        <li v-for="match in categoryMatches" :key="match.id" class="vbta-suggestion"
          @click="onMatch(match)" @mousedown="$event.preventDefault()" @mouseover="handleMouseOver($event)">
          <span class="icon is-size-7">
            <font-awesome-icon icon="filter" />
          </span>
          <span v-html="match.match"></span>
        </li>
      </ul>
    </div>
  </span>
</template>

<script>
import _ from 'lodash';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import Types from '../foodItem-types';

export default {
  name: 'search-bar',
  props: {
    matchesFound: {
      type: Object,
      required: true,
    },
    limit: {
      type: Number,
      default: 5,
      required: false,
    },
    name: {
      type: String,
      required: false,
    },
    async: {
      type: Boolean,
      default: true,
      required: false,
    },
    placeholder: {
      type: String,
      default() { return ''; },
      required: false,
    },
  },
  data() {
    return {
      query: '',
      foodItemMatches: [],
      categoryMatches: [],
      firstTouch: true,
      preselected: 0,
    };
  },
  watch: {
    query(value) {
      if (this.async) {
        _.debounce(this.getMatches, 150)(value);
      } else {
        this.getMatches(value);
      }

      this.$emit('change', value);
    },
    matchesFound(values) {
      this.foodItemMatches = [];
      this.categoryMatches = [];

      if (!this.query || values.query !== this.query ||
          (values.foodItems.length === 0 && values.categories.length === 0)) {
        return;
      }

      const foodItemMatches = [];
      const categoryMatches = [];
      values.foodItems.forEach((value) => {
        // TODO attempting to duplicate backend regix logic, have backend
        // return index?
        const index = value.name.search(new RegExp(`\\b${this.query}`, 'i'));
        // TODO assert index !== -1
        const substr1 = value.name.substring(0, index);
        const substr2 = `<strong>${value.name.slice(index, index + this.query.length)}</strong>`;
        const substr3 = value.name.substring(index + this.query.length);

        const match = substr1 + substr2 + substr3;

        if (index === 0) {
          foodItemMatches.unshift({ match, ...value });
        } else {
          foodItemMatches.push({ match, ...value });
        }
      }, this);

      values.categories.forEach((value, index) => {
        // Categories can match from the start for now
        const substr1 = `<strong>${value.slice(0, this.query.length)}</strong>`;
        const substr2 = value.substring(this.query.length);
        const match = substr1 + substr2;
        categoryMatches.push({ match, filter: value, id: index });  // dummy id for v-for key
      }, this);

      this.foodItemMatches = foodItemMatches;
      this.categoryMatches = categoryMatches;
      this.preselected = 0;
    },
  },
  methods: {
    handleDelete() {
      this.preselected = 0;
      this.firstTouch = true;
    },
    handleKeyUp() {
      if ((this.foodItemMatches.length || this.categoryMatches.length) && this.preselected !== 0) {
        const currselected = this.preselected;
        this.preselected = this.preselected - 1;

        const el = document.getElementsByClassName('vbta-suggestion')[this.preselected];
        el.style['background-color'] = '#00d1b2';

        const prev = document.getElementsByClassName('vbta-suggestion')[currselected];
        prev.style['background-color'] = '';
      }
    },
    handleKeyDown() {
      if ((this.foodItemMatches.length || this.categoryMatches.length) &&
           this.preselected !== ((this.foodItemMatches.length + this.categoryMatches.length) - 1)) {
        const currselected = this.preselected;
        if (!this.firstTouch) {
          this.preselected = this.preselected + 1;
        } else {
          this.firstTouch = false;
        }

        const el = document.getElementsByClassName('vbta-suggestion')[this.preselected];
        el.style['background-color'] = '#00d1b2';

        if (this.preselected !== 0) {
          const prev = document.getElementsByClassName('vbta-suggestion')[currselected];
          prev.style['background-color'] = '';
        }
      }
    },
    handleMouseOver() {
      if (!this.firstTouch) {
        const elKeyboardSelected = document.getElementsByClassName('vbta-suggestion')[this.preselected];
        elKeyboardSelected.style['background-color'] = '';
        this.preselected = 0;
        this.firstTouch = true;
      }
    },
    handleBlur() {
      this.clear();
    },
    handleEsc() {
      this.clear();
    },
    clear() {
      this.query = '';
      this.preselected = 0;
      this.foodItemMatches = [];
      this.categoryMatches = [];
      this.firstTouch = true;
    },
    onMatch(val) {
      let value = val;
      if (!value) {
        if (this.preselected < this.foodItemMatches.length) {
          value = this.foodItemMatches[this.preselected];
        } else if (this.preselected < this.foodItemMatches.length + this.categoryMatches.length) {
          value = this.categoryMatches[this.preselected - this.foodItemMatches.length];
        }
      }

      if (value) {
        this.clear();

        // Take away focus so mobile will close the keyboard
        this.$refs.inputBox.blur();

        this.$emit('match', { ...value });
      }
    },
    getMatches(query) {
      if (!query) {
        this.foodItemMatches = [];
        this.categoryMatches = [];
      }

      this.$emit('getMatches', query);
    },
    // TODO make this a mixin, it's in browse too
    typeIcon(id) {
      return Types.getType(id).icon;
    },
  },
  components: {
    'font-awesome-icon': FontAwesomeIcon,
  },
};
</script>

<style lang="scss" scoped>
@import "~bulma/sass/utilities/_all";
@import "~bulma/sass/elements/form.sass";

.vbta {
  width: 100%;
  position: relative;
  display: inline-block;
}

.vbta-input {
  background-color: transparent;
}

.vbta-menu.visible {
  display: inline-block;
}

.vbta-menu {
  position: absolute;
  width: 100%;
  top: 100%;
  left: 0;
  z-index: 1000;
  display: none;
  float: left;
  min-width: 160px;
  @include tablet {
    min-width: 20rem;
  }
  padding: 5px 0;
  margin: 2px 0 0;
  list-style: none;
  font-size: 14px;
  text-align: left;
  background-color: #ffffff;
  border: 1px solid #cccccc;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  -webkit-box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
  background-clip: padding-box;
}

.vbta-suggestion {
  // TODO DG added overflow but still needs work
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  flex-shrink: unset;
  display: block;
  padding: 3px 20px;
  clear: both;
  font-weight: normal;
  line-height: 1.42857143;
  color: #333333;
}

.vbta-suggestion:hover,
.vbta-suggestion:focus {
  color: #ffffff;
  text-decoration: none;
  outline: 0;
  background-color: #00d1b2;
  cursor: pointer;
}

.divider {
  background-color: #dbdbdb;
  border: none;
  display: block;
  height: 1px;
  margin: 0.5rem 0;
}

</style>



// WEBPACK FOOTER //
// src/components/SearchBar.vue